/**
 * ActionConfirmCard — editorial inline-card that appears in the message
 * stream when Clio has staged a multi-step action (`create_task`; the
 * `add_circle_member` card went with the People address book, wave w1023c).
 * The parent confirms or cancels here; the actual
 * mutation is committed by `useChat.confirmAction`, which then surfaces a
 * toast with an Undo affordance.
 *
 * Editorial recipe:
 *   - left rail in the category tint (indigo for a task)
 *   - mono uppercase eyebrow
 *   - serif headline ("Add this to your task queue?")
 *   - body rows in label/value pairs (mono label, plain value)
 *   - indigo CTA + ghost Cancel
 *
 * Once confirmed, the card flips into a quiet "Done" state for 1.5s and
 * is then cleared by the hook. The Undo lives on the toast — not here.
 */
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MonoLabel } from '@/components/editorial';
import { CheckCircle, ClipboardList, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PendingAction } from '@/types/chat';
import { formatPreset } from '@/i18n/intl';
import { useUiLanguage } from '../language';

interface ActionConfirmCardProps {
  action: PendingAction;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/** #2109 R3-B5: in the UI language (it followed the browser's before), through the Intl helpers. */
function formatDateTime(iso: string, locale: string): string {
  try {
    return formatPreset(iso, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }, { locale });
  } catch {
    return iso;
  }
}

export function ActionConfirmCard({ action, onConfirm, onCancel, isLoading = false }: ActionConfirmCardProps) {
  const { t } = useTranslation('chat');
  const uiLanguage = useUiLanguage();
  const Icon = ClipboardList;
  const confirmed = action.confirmed ?? false;

  // Tints follow the editorial categorical palette: indigo for "default
  // companion thing" (tasks Stevens/Clio organises).
  const railClass = 'bg-stevens-indigo';
  const iconTint = 'text-stevens-indigo';
  const iconBg = 'bg-stevens-indigo/10';

  const eyebrow = t('actionCard.task.eyebrow');
  const headline = confirmed ? t('actionCard.task.done') : t('actionCard.task.ask');

  return (
    <div className="flex gap-3 justify-start mt-4">
      {/* Spacer matching the assistant avatar so the card aligns with bubbles. */}
      <div className="w-8 flex-shrink-0" />

      <Card
        asChild
        className={cn(
          'relative w-full max-w-[80%] md:max-w-[70%] overflow-hidden transition-colors shadow-none',
          confirmed ? 'border-border/40 bg-muted/30' : 'border-border',
        )}
      >
      <article>
        {/* Left rail — colour fades once confirmed so the card recedes. */}
        <span
          className={cn(
            'absolute left-0 top-0 h-full w-[3px] transition-colors',
            confirmed ? 'bg-status-ok-fg' : railClass,
          )}
          aria-hidden
        />

        <header className="flex items-start gap-3 px-5 pt-4 pb-3">
          <span className={cn('inline-flex h-9 w-9 items-center justify-center rounded-full flex-shrink-0', iconBg)}>
            {confirmed ? (
              <CheckCircle className="h-4 w-4 text-status-ok-fg" />
            ) : (
              <Icon className={cn('h-4 w-4', iconTint)} />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <MonoLabel className="block mb-1">
              {eyebrow}
            </MonoLabel>
            <h4
              className={cn('font-serif',
                'text-lg leading-tight',
                confirmed && 'text-stevens-ink-3 line-through decoration-status-ok-fg/40 decoration-[1px]',
              )}
            >
              {headline}
            </h4>
          </div>
        </header>

        <dl className="px-5 pb-3 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 text-body">
          <>
              <Row label={t('actionCard.fields.title')} value={action.data.title} />
              {action.data.category && (
                <Row label={t('actionCard.fields.category')}>
                  <MonoLabel className="inline-flex items-center gap-1.5 bg-muted/60 px-2 py-0.5 rounded">
                    {action.data.category.replace(/_/g, ' ')}
                  </MonoLabel>
                </Row>
              )}
              {action.data.date_time && <Row label={t('actionCard.fields.when')} value={formatDateTime(action.data.date_time, uiLanguage)} />}
              {action.data.duration_minutes && (
                <Row label={t('actionCard.fields.duration')} value={t('actionCard.minutes', { count: Number(action.data.duration_minutes) })} />
              )}
              {action.data.description && <Row label={t('actionCard.fields.note')} value={action.data.description} />}
          </>
        </dl>

        {confirmed ? null : (
          <footer className="flex items-center justify-end gap-2 border-t border-border/60 px-5 py-3 bg-muted/20">
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={isLoading}>
              <X className="h-3.5 w-3.5 mr-1.5" />
              {t('actionCard.notNow')}
            </Button>
            <Button
              size="sm"
              variant="brand"
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5 mr-1.5" />}
              {t('actionCard.task.confirm')}
            </Button>
          </footer>
        )}
      </article>
      </Card>
    </div>
  );
}

function Row({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <>
      <dt className="pt-0.5">
        <MonoLabel>{label}</MonoLabel>
      </dt>
      <dd className="text-foreground break-words">{children ?? value}</dd>
    </>
  );
}
